import SvgAnglesDown from "@/styles/icons/AnglesDown";
import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { useMediaQuery } from "@/hooks/useMediaQuery";

type Props = {
  data: any;
  pageData: any;
};

export default function TableOfContents({ data, pageData }: Props) {
  const [sections, setSections] = useState<HTMLElement[]>([]);
  const [scrolled, setScrolled] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [isAccordionOpen, setIsAccordionOpen] = useState(!isMobile); // Closed on mobile, open on desktop
  const [userManuallyOpened, setUserManuallyOpened] = useState(false);
  const headerHeight = 64;

  useEffect(() => {
    const timeout = setTimeout(() => {
      const sectionElements = Array.from(
        document.querySelectorAll("section[id]")
      ) as HTMLElement[];

      const filteredSections = sectionElements.filter(
        (el) => el.getAttribute("id") !== "Consultation"
      );
      setSections(filteredSections);
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  // Update accordion state when screen size changes
  useEffect(() => {
    setIsAccordionOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setScrolled(scrollY > 10);
      
      // Auto-close accordion when scrolling to TOC
      // But only if user didn't manually open it
      if (scrollY > 10 && !userManuallyOpened) {
        const toc = document.getElementById("table-of-contents-id");
        if (toc) {
          const tocRect = toc.getBoundingClientRect();
          // If TOC is visible on screen and accordion is open, close it
          if (tocRect.top <= headerHeight + 100 && isAccordionOpen) {
            setIsAccordionOpen(false);
          }
        }
      }
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isAccordionOpen, userManuallyOpened]);

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string
  ) => {
    e.preventDefault();
    const target = document.getElementById(sectionId);
    const toc = document.getElementById("table-of-contents-id");

    if (target && toc) {
      // Close accordion before navigation
      setIsAccordionOpen(false);
      // Reset manual open flag
      setUserManuallyOpened(false);
      
      setTimeout(() => {
        const tocRect = toc.getBoundingClientRect();
        const tocHeight = toc.offsetHeight;
        const tocTop = tocRect.top;

        const y =
          target.getBoundingClientRect().top +
          window.scrollY -
          tocHeight -
          tocTop;

        window.scrollTo({ top: y, behavior: "smooth" });
      }, 1500); // timeout 1500ms
    }
  };

  return (
    <>
      <div
        id="table-of-contents-id"
        className={`flex flex-col bg-accent-color-two text-accent-color-three-dark
        max-h-[calc(100vh-120px)] overflow-scroll md:max-h-none md:overflow-visible
        desktop:py-4 desktop:px-32 md:py-4 md:px-12 py-4 px-6
        ${scrolled ? "py-4 px-6" : ""}
      `}
        style={{
          position: "sticky",
          top: headerHeight,
          zIndex: 20,
        }}
      >
        <Accordion 
          type="single" 
          collapsible 
          value={isAccordionOpen ? "table-of-contents" : ""}
          onValueChange={(value) => {
            const isOpen = value === "table-of-contents";
            setIsAccordionOpen(isOpen);
            setUserManuallyOpened(isOpen);
          }}
        >
          <AccordionItem
            value="table-of-contents"
            className="border-none bg-transparent"
          >
            <AccordionTrigger
              hideIcon
              headerAsChild={true}
              className={`uppercase text-center p-0
              ${scrolled ? "text-[20px]" : "text-special-h2"}
            `}
            >
              <span className="w-full text-center flex relative items-center justify-center">
                Table of contents
                <SvgAnglesDown className="absolute right-0 h-6 w-6 shrink-0 transition-transform duration-200" />
              </span>
            </AccordionTrigger>
            <AccordionContent wrapperClassName="flex flex-col desktop:flex-row gap-4 pt-12 flex-wrap justify-center">
              {sections
                .map((section) => {
                  const sectionId = section.getAttribute("id");
                  const sectionName = section.getAttribute("data-name");

                  if (!sectionId || !sectionName) return null;

                  return {
                    sectionId,
                    sectionName,
                  };
                })
                .filter(Boolean)
                .map((section, index) => {
                return (
                  <a
                    key={`toc-${index}-mobile`}
                    href={`#${section.sectionId
                      ?.replaceAll(".", "-")
                      .replaceAll(/[^a-zA-Z0-9-_]/g, "-")}`}
                    onClick={(e) =>
                      handleClick(
                        e,
                        section.sectionId
                          ?.replaceAll(".", "-")
                          .replaceAll(/[^a-zA-Z0-9-_]/g, "-") || ""
                      )
                    }
                    className={`font-sans uppercase border border-accent-color-three-dark px-4 py-1 w-full desktop:w-fit hover:underline
                    ${
                      scrolled
                        ? "text-[16px] leading-5"
                        : "text-[16px] leading-6 tracking-[0.15em]"
                    }
                  `}
                  >
                    {index + 1}. {section.sectionName}
                  </a>
                );
              })}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </>
  );
}
